// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js'
import type { Database } from './types'

const SUPABASE_URL =
	import.meta.env.VITE_SUPABASE_URL ||
	'https://ckqgofnbfduuxawcrkrq.supabase.co'
const SUPABASE_PUBLISHABLE_KEY =
	import.meta.env.VITE_SUPABASE_KEY ||
	'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNrcWdvZm5iZmR1dXhhd2Nya3JxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzg0Mjg2NzgsImV4cCI6MjA1NDAwNDY3OH0.aybnQjpw9QDjxdLwSrhceSIcjxKsnRXgWCG2ACKtvXg'

// Configuration constants
// Use a longer default timeout (15 s) to accommodate slower mobile networks.
// Allow override via VITE_SUPABASE_TIMEOUT_MS.
const FETCH_TIMEOUT_MS =
	(typeof import.meta.env.VITE_SUPABASE_TIMEOUT_MS === 'string'
		? parseInt(import.meta.env.VITE_SUPABASE_TIMEOUT_MS as string, 10)
		: NaN) || 15000 // 15 seconds
const SESSION_STORAGE_KEY = 'shared-sb-auth-token'

// Type for session data structure
interface SharedSessionData {
	access_token: string
	refresh_token: string
	expires_at?: number
	user?: any
}

// Singleton pattern to ensure only one client instance
let supabaseClient: ReturnType<typeof createClient<Database>> | null = null

const createSupabaseClient = () => {
	if (supabaseClient) {
		return supabaseClient
	}

	supabaseClient = createClient<Database>(
		SUPABASE_URL,
		SUPABASE_PUBLISHABLE_KEY,
		{
			auth: {
				autoRefreshToken: true,
				persistSession: true,
				detectSessionInUrl: true,
				storage: {
					// OAuth-aware storage wrapper
					// Critical: OAuth flow stores temporary state/PKCE data that must NOT be validated or deleted
					getItem: (key: string) => {
						try {
							const item = localStorage.getItem(key)
							if (!item) return null

							// OAuth state and PKCE verifier must pass through unchanged
							// These are temporary tokens used during OAuth flow and don't have session structure
							const isOAuthData =
								key.includes('-code-verifier') ||
								key.includes('-oauth-state') ||
								key.includes('-auth-token-code-verifier')

							if (isOAuthData) {
								// Return raw OAuth data without validation
								return item
							}

						// Return session tokens as-is and let Supabase handle refresh
						// Do not delete expired sessions - autoRefreshToken will handle renewal
						return item
						} catch (error) {
							// Don't delete on error - could be OAuth data
							return localStorage.getItem(key)
						}
					},
					setItem: (key: string, value: string) => {
						try {
							const isOAuthData =
								key.includes('-code-verifier') ||
								key.includes('-oauth-state') ||
								key.includes('-auth-token-code-verifier')

							localStorage.setItem(key, value)
						} catch (error) {
							// Try to store anyway - don't validate on write
							try {
								localStorage.setItem(key, value)
							} catch (retryError) {
								// Silently fail on retry
							}
						}
					},
					removeItem: (key: string) => {
						const isOAuthData =
							key.includes('-code-verifier') ||
							key.includes('-oauth-state') ||
							key.includes('-auth-token-code-verifier')

						localStorage.removeItem(key)
					},
				},
			},
			global: {
				// Add fetch timeout to prevent hanging requests
				fetch: (url: RequestInfo | URL, options?: RequestInit) => {
					const fetchStartTime = Date.now()

					// Use a uniform timeout for all requests. A shorter 5 s limit on /auth/v1/*
					// was aborting token exchanges on slower networks (mobile 5 G). We now
					// rely on the standard FETCH_TIMEOUT_MS for every endpoint.
					const urlString = url.toString()
					const timeout = FETCH_TIMEOUT_MS

					const controller = new AbortController()
					const timeoutId = setTimeout(() => {
						controller.abort()
					}, timeout)

					return fetch(url, {
						...options,
						signal: controller.signal,
					})
						.then((response) => {
							const fetchTime = Date.now() - fetchStartTime

							clearTimeout(timeoutId)
							return response
						})
						.catch((error) => {
							const errorTime = Date.now() - fetchStartTime
							clearTimeout(timeoutId)
							if (error.name === 'AbortError') {
								throw new Error(
									`Request timeout after ${timeout}ms - please check your connection`
								)
							}
							throw error
						})
				},
			},
		}
	)

	return supabaseClient
}

export const supabase = createSupabaseClient()

// Helper to check if current domain is a subdomain
const isSubdomain = (): boolean => {
	if (typeof window === 'undefined') return false

	const hostname = window.location.hostname

	// localhost variations
	if (hostname === 'localhost' || hostname === '127.0.0.1') return false
	if (hostname.endsWith('.localhost')) return true

	// Standard domain check (has more than 2 parts)
	const parts = hostname.split('.')
	return parts.length > 2
}

// Validate session data structure
const isValidSessionData = (data: any): data is SharedSessionData => {
	return (
		data &&
		typeof data === 'object' &&
		typeof data.access_token === 'string' &&
		typeof data.refresh_token === 'string' &&
		data.access_token.length > 0 &&
		data.refresh_token.length > 0
	)
}

// Manual session transfer mechanism for cross-domain auth
export const transferSessionFromMainDomain = async () => {
	if (!isSubdomain()) {
		return false
	}

	try {
		// Try to get existing session first
		const {
			data: { session: existingSession },
		} = await supabase.auth.getSession()

		if (existingSession) {
			return true
		}

		// Look for shared session data more efficiently
		const sessionData = localStorage.getItem(SESSION_STORAGE_KEY)

		if (sessionData) {
			try {
				const parsedSession = JSON.parse(sessionData)

				// Validate session data structure
				if (!isValidSessionData(parsedSession)) {
					localStorage.removeItem(SESSION_STORAGE_KEY)
					return false
				}

				// Use setSession to restore the session
				const { data, error } = await supabase.auth.setSession({
					access_token: parsedSession.access_token,
					refresh_token: parsedSession.refresh_token,
				})

				if (error) {
					return false
				}

				return true
			} catch (parseError) {
				// Clear invalid session data
				localStorage.removeItem(SESSION_STORAGE_KEY)
				return false
			}
		}

		return false
	} catch (error) {
		return false
	}
}

// Save session to shared storage when it changes
export const saveSessionToSharedStorage = async () => {
	if (typeof window === 'undefined') return

	try {
		const {
			data: { session },
		} = await supabase.auth.getSession()

		if (session) {
			const sessionData: SharedSessionData = {
				access_token: session.access_token,
				refresh_token: session.refresh_token,
				expires_at: session.expires_at,
				user: session.user,
			}

			localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData))
		} else {
			localStorage.removeItem(SESSION_STORAGE_KEY)
		}
	} catch (error) {
		// Silently fail - session saving is not critical
	}
}

// Legacy function for compatibility
export const syncSessionAcrossDomains = transferSessionFromMainDomain
